import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

export default function Register({ navigate }) {
  const { login } = useAuth();

  // ── Stage: 'form' | 'otp' ──────────────────────────────────────────────
  const [stage,   setStage]   = useState('form');
  const [userId,  setUserId]  = useState('');
  const [regEmail,setRegEmail]= useState('');

  // ── Form state ──────────────────────────────────────────────────────────
  const [form, setForm]       = useState({ name:'', email:'', password:'', confirm:'' });
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw]   = useState(false);
  const [showCf, setShowCf]   = useState(false);
  const update = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  // ── OTP state ───────────────────────────────────────────────────────────
  const [otp,        setOtp]        = useState(['','','','','','']);
  const [otpError,   setOtpError]   = useState('');
  const [verifying,  setVerifying]  = useState(false);
  const [resendMsg,  setResendMsg]  = useState('');
  const [resendCool, setResendCool] = useState(0);  // countdown seconds
  const otpRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  // Countdown timer for resend
  useEffect(() => {
    if (resendCool <= 0) return;
    const t = setTimeout(() => setResendCool(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCool]);

  // ── Step 1: Register ────────────────────────────────────────────────────
  const submitRegister = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    setError(''); setLoading(true);
    try {
      const res  = await fetch('/api/auth/register', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name, email: form.email, password: form.password }),
      });
      const data = await res.json();
      if (res.ok && data.requiresVerification) {
        setUserId(data.userId);
        setRegEmail(data.email);
        setStage('otp');
        setResendCool(60);
      } else if (!res.ok) {
        setError(data.error || 'Registration failed');
      }
    } catch { setError('Connection error — is the server running?'); }
    setLoading(false);
  };

  // ── OTP input handling ──────────────────────────────────────────────────
  const handleOtpChange = (i, val) => {
    const clean = val.replace(/\D/g,'').slice(0,1);
    const next = [...otp];
    next[i] = clean;
    setOtp(next);
    setOtpError('');
    if (clean && i < 5) otpRefs[i+1].current?.focus();
  };

  const handleOtpKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) {
      otpRefs[i-1].current?.focus();
    }
    if (e.key === 'ArrowLeft' && i > 0) otpRefs[i-1].current?.focus();
    if (e.key === 'ArrowRight' && i < 5) otpRefs[i+1].current?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g,'').slice(0,6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
      otpRefs[5].current?.focus();
    }
  };

  // ── Step 2: Verify OTP ──────────────────────────────────────────────────
  const submitOTP = async () => {
    const code = otp.join('');
    if (code.length < 6) { setOtpError('Enter all 6 digits'); return; }
    setVerifying(true); setOtpError('');
    try {
      const res  = await fetch('/api/auth/verify-otp', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, otp: code }),
      });
      const data = await res.json();
      if (res.ok) {
        login(data.token, data.user);
      } else {
        setOtpError(data.error || 'Verification failed');
        setOtp(['','','','','','']);
        otpRefs[0].current?.focus();
      }
    } catch { setOtpError('Connection error'); }
    setVerifying(false);
  };

  // ── Resend OTP ──────────────────────────────────────────────────────────
  const resendOTP = async () => {
    if (resendCool > 0) return;
    setResendMsg(''); setOtpError('');
    try {
      const res  = await fetch('/api/auth/resend-otp', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });
      const data = await res.json();
      if (res.ok) {
        setResendMsg('New code sent!');
        setResendCool(60);
        setOtp(['','','','','','']);
        otpRefs[0].current?.focus();
        setTimeout(() => setResendMsg(''), 3000);
      } else {
        setOtpError(data.error || 'Failed to resend');
      }
    } catch { setOtpError('Connection error'); }
  };

  // ── OTP SCREEN ──────────────────────────────────────────────────────────
  if (stage === 'otp') return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-logo">
          <div className="auth-logo-icon">🧬</div>
          <div style={{ fontSize:13, fontWeight:600, color:'var(--text-3)', letterSpacing:1.5, textTransform:'uppercase' }}>
            Enpower <span style={{ color:'var(--blue)' }}>Org DNA</span>
          </div>
        </div>

        <div style={{ textAlign:'center', marginBottom:24 }}>
          <div style={{ fontSize:48, marginBottom:12 }}>📧</div>
          <div className="auth-title" style={{ marginBottom:8 }}>Check your email</div>
          <div className="auth-sub">
            We sent a 6-digit code to<br />
            <strong style={{ color:'var(--text)' }}>{regEmail}</strong>
          </div>
        </div>

        {otpError && (
          <div className="form-error" style={{ marginBottom:16, textAlign:'center' }}>{otpError}</div>
        )}
        {resendMsg && (
          <div style={{ padding:'10px 14px', background:'rgba(22,163,74,0.1)', border:'1px solid rgba(22,163,74,0.3)', borderRadius:8, marginBottom:16, textAlign:'center', fontSize:13, color:'var(--green)' }}>
            {resendMsg}
          </div>
        )}

        {/* 6-box OTP input */}
        <div style={{ display:'flex', gap:10, justifyContent:'center', marginBottom:28 }} onPaste={handleOtpPaste}>
          {otp.map((digit, i) => (
            <input
              key={i}
              ref={otpRefs[i]}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              autoFocus={i === 0}
              onChange={e => handleOtpChange(i, e.target.value)}
              onKeyDown={e => handleOtpKeyDown(i, e)}
              style={{
                width:46, height:56,
                textAlign:'center',
                fontSize:24, fontWeight:800,
                border:`2px solid ${digit ? 'var(--blue)' : 'var(--border)'}`,
                borderRadius:10,
                background:'var(--bg2)',
                color:'var(--text)',
                outline:'none',
                transition:'border-color 0.15s',
                fontFamily:'var(--ff-m)',
              }}
            />
          ))}
        </div>

        <button
          className="btn btn-primary btn-lg"
          style={{ width:'100%', justifyContent:'center', marginBottom:16 }}
          disabled={verifying || otp.join('').length < 6}
          onClick={submitOTP}
        >
          {verifying
            ? <><span className="loader loader-sm" style={{ borderTopColor:'#fff' }}></span> Verifying…</>
            : '✓ Verify email'}
        </button>

        <div style={{ textAlign:'center', fontSize:13, color:'var(--text-3)', marginBottom:8 }}>
          Didn't receive it?{' '}
          {resendCool > 0
            ? <span style={{ color:'var(--text-4)' }}>Resend in {resendCool}s</span>
            : <span className="auth-link" onClick={resendOTP} style={{ cursor:'pointer' }}>Resend code</span>
          }
        </div>

        <div style={{ textAlign:'center', fontSize:12, color:'var(--text-4)' }}>
          <span className="auth-link" style={{ cursor:'pointer' }} onClick={() => { setStage('form'); setOtp(['','','','','','']); }}>
            ← Change email address
          </span>
        </div>
      </div>
    </div>
  );

  // ── REGISTER FORM ───────────────────────────────────────────────────────
  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-logo">
          <div className="auth-logo-icon">🧬</div>
          <div style={{ fontSize:13, fontWeight:600, color:'var(--text-3)', letterSpacing:1.5, textTransform:'uppercase' }}>
            Enpower <span style={{ color:'var(--blue)' }}>Org DNA</span>
          </div>
        </div>

        <div className="auth-title">Create your account</div>
        <div className="auth-sub">Start your organizational diagnostic in minutes</div>

        <form onSubmit={submitRegister}>
          {error && <div className="form-error" style={{ marginBottom:18 }}>{error}</div>}
          <div className="form-group">
            <label className="form-label">Full name</label>
            <input className="form-input" type="text" placeholder="Rajesh Sharma" value={form.name} onChange={update('name')} required autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">Work email</label>
            <input className="form-input" type="email" placeholder="you@company.com" value={form.email} onChange={update('email')} required />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <div style={{ position:'relative' }}>
              <input className="form-input" type={showPw?'text':'password'} placeholder="Minimum 8 characters"
                value={form.password} onChange={update('password')} required style={{ paddingRight:40 }} />
              <span onClick={() => setShowPw(p => !p)} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', cursor:'pointer', fontSize:16, color:'var(--text-3)', userSelect:'none' }}>
                {showPw ? '🙈' : '👁'}
              </span>
            </div>
          </div>
          <div className="form-group" style={{ marginBottom:24 }}>
            <label className="form-label">Confirm password</label>
            <div style={{ position:'relative' }}>
              <input className="form-input" type={showCf?'text':'password'} placeholder="Repeat password"
                value={form.confirm} onChange={update('confirm')} required style={{ paddingRight:40 }} />
              <span onClick={() => setShowCf(p => !p)} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', cursor:'pointer', fontSize:16, color:'var(--text-3)', userSelect:'none' }}>
                {showCf ? '🙈' : '👁'}
              </span>
            </div>
          </div>
          <button type="submit" className="btn btn-primary btn-lg" style={{ width:'100%', justifyContent:'center' }} disabled={loading}>
            {loading ? <><span className="loader loader-sm" style={{ borderTopColor:'#fff' }}></span> Creating account…</> : 'Create account →'}
          </button>
        </form>

        <div className="auth-footer">
          Already have an account?{' '}
          <span className="auth-link" onClick={() => navigate('/login')}>Sign in</span>
        </div>
        <div style={{ marginTop:16, fontSize:12, color:'var(--text-4)', textAlign:'center' }}>
          By creating an account you agree to our Terms of Service
        </div>
      </div>
    </div>
  );
}
